import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, of, shareReplay } from 'rxjs';

/**
 * Unified service for loading and caching SVG assets
 * Handles both HTTP caching (request deduplication) and memory caching
 */
@Injectable({ providedIn: 'root' })
export class SvgCacheService {
  private readonly http = inject(HttpClient);
  private readonly memoryCache = new Map<string, string>();
  private readonly httpCache = new Map<string, Observable<string | undefined>>();

  getSvg(assetName: string, set = 'icons'): Observable<string | undefined> {
    // Check memory cache first
    const memCached = this.memoryCache.get(assetName);
    if (memCached) {
      return of(memCached);
    }

    // Check if HTTP request is already in progress
    const httpCached = this.httpCache.get(assetName);
    if (httpCached) {
      return httpCached;
    }

    // Load from HTTP
    const url = `assets/${set}/${assetName}.svg`;
    const request = this.http.get(url, { responseType: 'text' }).pipe(
      shareReplay({ bufferSize: 1, refCount: true }),
      catchError((error) => {
        console.error(`Error loading SVG ${assetName}:`, error);
        this.httpCache.delete(assetName);
        return of(undefined);
      })
    );

    this.httpCache.set(assetName, request);

    return request;
  }

  /**
   * Load SVG and cache it in memory after successful load
   * Used internally by the asset component
   */
  getSvgWithMemoryCache(assetName: string, set = 'icons'): Observable<string | undefined> {
    const memCached = this.memoryCache.get(assetName);
    if (memCached) {
      return of(memCached);
    }

    return this.getSvg(assetName, set);
  }

  /**
   * Cache a loaded SVG in memory
   * Called after successful load to avoid HTTP requests for frequently used assets
   */
  cacheInMemory(assetName: string, svg: string): void {
    this.memoryCache.set(assetName, svg);
  }
}
